import {ORBIT_VERSION} from '../orbit-version';
import {Check} from '@sparkware/app-utils';
import {
  B2C_APP_ID,
  GetGamesInfoResponse,
  IClientInfo,
  IOrbitApi,
  ISiteInfo,
  IUserInfo,
  PartyJackpotInfo
} from './orbit-api.interface';
import {IServiceFactoryExtended} from './services/service-factory-extended.interface';
import { IBlueRibbonPartyJackpot } from '../common/interfaces/blue-ribbon-party-jackpot.interface';
export class OrbitApi implements IOrbitApi {
  constructor(private _services: IServiceFactoryExtended) {
  }

  get version() {

    return ORBIT_VERSION;
  }

  get userInfo(): IUserInfo {
    return {
      isAuthorized: this._services.user.isAuthorized
    };
  }
  get siteInfo(): ISiteInfo {
    return {
      brandId: this._services.sdk.getBrandID(),
      subBrandId: this._services.sdk.getSubBrandID()
    }
  }

  get clientInfo(): IClientInfo {
    return {
      fullVersion: this._services.sdk.getSkin().getVersion()
    }
  }

  /**
   * Will return an array of gameTypes which has allowWithdrawOnlyFromReal === true
   */
  async getGamesWithoutBonus() {
    const gamesTypes = this._services.configuration.usedGameTypesBySite;

    if(gamesTypes?.length > 0){
      const gamesInfo = await this._services.sdk.getGamesInfo(gamesTypes);

      return Object.keys(gamesInfo)
          .filter(gameType => gamesInfo[gameType].allowWithdrawOnlyFromReal === true)
          .reduce((acc, gameType) => {
            acc[gameType] = gamesInfo[gameType];
            return acc;
          }, {});
    }

    return {};
  }

  getErrorLogs() {
    return this._services.logger.errorLogs;
  }

  deepLink(deepLinkObject: any, navigate?: any) {
    return this._services.deepLink.executeDeepLink(deepLinkObject, navigate);
  }

  getMarketingData(marketingParams) {
    if (Check.isString(marketingParams)) {
      try {
        marketingParams = JSON.parse(marketingParams);
      } catch (err) {
        this._services.logger.error(`Failed to parse marketing params: ${marketingParams}`, err);
      }
    }

    return this._services.marketing.getMarketingData(marketingParams);
  }

  sendAnalytics(category?: string, action?: string, label?: string, value?: string) {
    this._services.analytics.sendAnalytics(category, action, label, value);
  }

  sendElasticInfoEvent(eventType:string, category: string, action: string, label: string, value?: string, customData: Object = null, sendToDWH: boolean = true, appID: string = B2C_APP_ID): boolean {
    if (!Check.isString(eventType) ||
      (!Check.isNullOrUndefined(category) && !Check.isString(category)) ||
      (!Check.isNullOrUndefined(action) && !Check.isString(action)) ||
      (!Check.isNullOrUndefined(label) && !Check.isString(label))) {
      return false;
    }

    const MAX_FIELD_SIZE = 100;
    eventType = (eventType?.length > MAX_FIELD_SIZE) ? eventType.slice(0, MAX_FIELD_SIZE) : eventType;
    category = (category?.length > MAX_FIELD_SIZE) ? category.slice(0, MAX_FIELD_SIZE) : category;
    action = (action?.length > MAX_FIELD_SIZE) ? action.slice(0, MAX_FIELD_SIZE) : action;
    label = (label?.length > MAX_FIELD_SIZE) ? label.slice(0, MAX_FIELD_SIZE) : label;

    if (Check.isNullOrUndefined(customData)) {
      this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData, appID }, sendToDWH);
      return true;
    }

    if (Check.isString(customData)) {
      customData = (customData.length > MAX_FIELD_SIZE) ? customData.slice(0, MAX_FIELD_SIZE) : customData;
      this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData: appID ? { [appID + "_data"]: customData } : customData, appID, }, sendToDWH);
      return true;
    } else if (Check.isObject(customData)) {
      const MAX_FIELDS_COUNT = 10;
      let fieldsCount: number = 0;
      for (const prop in customData) {
        if (!Check.isString(customData[prop])) {
          return false; //only allowing string values in the customData fields - dismissing the event otherwise
        }
        if (customData[prop].length > MAX_FIELD_SIZE) {
          customData[prop] = customData[prop].slice(0, MAX_FIELD_SIZE);//truncate string to max 100 chars
        }
        fieldsCount++;
      }
      if (fieldsCount > MAX_FIELDS_COUNT) {
        return false; //too many custom fields, event dismissed
      }
      this._services.analytics.sendElasticInfoEvent({ eventType, category, action, label, value, customData: appID ? { [appID + "_data"]: customData } : customData, appID}, sendToDWH);
      return true;
    }

    return false;
  }

  showPwaPrompt() {
    return this._services.progressiveWebApp.prompt();
  }

  isPwaInstallable() {
    return this._services.progressiveWebApp.isPWAInstallable;
  }


  async getGamesInfo(gameTypes: number[]): Promise<GetGamesInfoResponse> {
    gameTypes = gameTypes || [];

    let foundGames = {};
    let missingGames = [];
    if (gameTypes.length === 0) {
      return {
        foundGames,
        missingGames,
      };
    }

    let gamesMap = this._services.games.gamesMap;
    if (Check.isEmpty(gamesMap)) {
      gamesMap = await this._services.games.getGamesInfoMap(gameTypes);
    }

    gameTypes.forEach(gameType => {
      const gameInfo = gamesMap[gameType];
      if (gameInfo) {
        foundGames[gameType] = {
          gameType: gameInfo.gameType,
          title: gameInfo.title,
          gameGroup: gameInfo.gameGroup,
          gameImage: gameInfo.getGameImage(),
          gameImageAlt: gameInfo.getImageSeoAlt()
        };
      } else {
        missingGames.push(gameType);
      }
    });

    return {
      foundGames,
      missingGames
    };
  }

  private _getPartyJackpotInfoFromEventData(jp: IBlueRibbonPartyJackpot): PartyJackpotInfo {
    const gamesList = this._services.configuration.getGamesListByJackpotId(jp.jackpotId);
    const partyJpName = gamesList?.title ?? "";
    const games = gamesList?.gamesId ?? [];
    return {
      id: jp.jackpotId,
      name: partyJpName,
      amount: this._services.currency.formatMoney(jp.currentValue),
      mustHitTime: jp.mustHitTime,
      isActive: jp.isActive,
      status: jp.potState,
      games: games
    } as PartyJackpotInfo;
  }

  onBlueRibbonPartyJackpotUpdate(fn: (data: PartyJackpotInfo) => void) {
    this._services.sdk.events.onBlueRibbonPartyJackpotStateChanged((data: IBlueRibbonPartyJackpot) => {
      if (Check.isFunction(fn)) {
        fn(this._getPartyJackpotInfoFromEventData(data));
      }
    });
  }

  getRefreshToken(): string {
    return this._services.sdk.getRefreshToken();
  }

}
